import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useResellerContext } from 'pages/Reseller/ResellerContext';

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatMonth = (month: string) => {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1, 1);
  return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
};

const ResellerReport = () => {
  const { reportData, loadingReport } = useResellerContext();
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  if (loadingReport) {
    return (
      <Stack gap="1rem" mt="1rem">
        <Skeleton h="8rem" rounded="lg" />
        <Skeleton h="14rem" rounded="lg" />
        <Skeleton h="6rem" rounded="lg" />
      </Stack>
    );
  }

  if (!reportData) {
    return (
      <Text color="gray.500" py={8} textAlign="center">
        Nenhum dado disponível.
      </Text>
    );
  }

  const { monthly_payments, charge_totals } = reportData;

  return (
    <Stack gap="1.5rem" mt="1rem">
      {/* Pagamentos por mês */}
      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Stack gap="1rem">
          <Text fontWeight="semibold" color="gray.600" fontSize="md">
            Pagamentos por Mês
          </Text>

          {monthly_payments.length === 0 ? (
            <Text color="gray.500" py={4} textAlign="center">
              Nenhum pagamento encontrado.
            </Text>
          ) : (
            <TableContainer overflowX="auto">
              <Table variant="detailed" size="sm">
                <Thead>
                  <Tr>
                    <Th textAlign="left">Mês</Th>
                    <Th textAlign="right">Total</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {monthly_payments.map((item) => (
                    <Tr key={item.month}>
                      <Td textTransform="capitalize">
                        {formatMonth(item.month)}
                      </Td>
                      <Td textAlign="right">{formatCurrency(item.total)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Box>

      {/* Totais por status de cobrança */}
      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Stack gap="1rem">
          <Text fontWeight="semibold" color="gray.600" fontSize="md">
            Totais por Status de Cobrança
          </Text>
          <Grid templateColumns="repeat(3, 1fr)" gap="1rem">
            <Box bg={cardBg} borderRadius="md" p="1rem" textAlign="center">
              <Text fontSize="xs" color="gray.500" mb="0.25rem">
                Em Aberto
              </Text>
              <Text fontWeight="bold" color="orange.500" fontSize="lg">
                {formatCurrency(charge_totals.pending)}
              </Text>
            </Box>
            <Box bg={cardBg} borderRadius="md" p="1rem" textAlign="center">
              <Text fontSize="xs" color="gray.500" mb="0.25rem">
                Pago
              </Text>
              <Text fontWeight="bold" color="green.500" fontSize="lg">
                {formatCurrency(charge_totals.paid)}
              </Text>
            </Box>
            <Box bg={cardBg} borderRadius="md" p="1rem" textAlign="center">
              <Text fontSize="xs" color="gray.500" mb="0.25rem">
                Cancelado
              </Text>
              <Text fontWeight="bold" color="red.500" fontSize="lg">
                {formatCurrency(charge_totals.canceled)}
              </Text>
            </Box>
          </Grid>
        </Stack>
      </Box>
    </Stack>
  );
};

export default ResellerReport;
